/**
 * Generated by orval v6.23.0 🍺
 * Do not edit manually.
 * APIs for RCI FE
 * This document describes the APIs provided by NeoAP BE for RCI FE, designed to be integrated into NeoAP FE using `iframe`.\
These APIs utilize cookie-based authentication. All API requests must include the cookie `_ap_session`.

 * OpenAPI spec version: 1
 */
import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import type {
  QueryFunction,
  QueryKey,
  UseQueryOptions,
  UseQueryResult,
  UseSuspenseQueryOptions,
  UseSuspenseQueryResult,
} from '@tanstack/react-query';
import type { BadRequestResponse } from '../model/badRequestResponse';
import type { ForbiddenResponse } from '../model/forbiddenResponse';
import type { NotFoundResponse } from '../model/notFoundResponse';
import type { SearchAdminInvoiceReportsParams } from '../model/searchAdminInvoiceReportsParams';
import type { SearchAdminInvoiceReportsRequest } from '../model/searchAdminInvoiceReportsRequest';
import type { SearchAdminInvoiceReportsResponse } from '../model/searchAdminInvoiceReportsResponse';
import type { UnauthorizedResponse } from '../model/unauthorizedResponse';
import type { UnprocessableEntityResponse } from '../model/unprocessableEntityResponse';
import { useCustomClient } from '../../mutator/custom-instance';
import type { ErrorType, BodyType } from '../../mutator/custom-instance';
import { customFomDataFn } from '../../api/formData/custom-function';

type AwaitedInput<T> = PromiseLike<T> | T;

type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;

// eslint-disable-next-line
type SecondParameter<T extends (...args: any) => any> = T extends (
  config: any,
  args: infer P
) => any
  ? P
  : never;

/**
 * Searches invoice reports from the administrator's perspective. <br/>
**Restricted Access**: Only accessible to administrators and observers. <br/>
**Feature Flag**: Requires `AdminInvoiceReportRenewalFeature` to be enabled.

 * @summary Get a list of admin invoice reports
 */
export const useSearchAdminInvoiceReportsHook = () => {
  const searchAdminInvoiceReports = useCustomClient<
    SearchAdminInvoiceReportsResponse | Blob
  >();

  return (
    searchAdminInvoiceReportsRequest: BodyType<SearchAdminInvoiceReportsRequest>,
    params?: SearchAdminInvoiceReportsParams,
    options?: SecondParameter<ReturnType<typeof useCustomClient>>
  ) => {
    return searchAdminInvoiceReports(
      {
        url: `/api/js/invoice_reports/admin_invoice_reports/search`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: searchAdminInvoiceReportsRequest,
        params,
      },
      options
    );
  };
};

export const getSearchAdminInvoiceReportsQueryKey = (
  searchAdminInvoiceReportsRequest: SearchAdminInvoiceReportsRequest,
  params?: SearchAdminInvoiceReportsParams
) => {
  return [
    `/api/js/invoice_reports/admin_invoice_reports/search`,
    ...(params ? [params] : []),
    searchAdminInvoiceReportsRequest,
  ] as const;
};

export const useSearchAdminInvoiceReportsQueryOptions = <
  TData = Awaited<
    ReturnType<ReturnType<typeof useSearchAdminInvoiceReportsHook>>
  >,
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
    | UnprocessableEntityResponse
  >
>(
  searchAdminInvoiceReportsRequest: SearchAdminInvoiceReportsRequest,
  params?: SearchAdminInvoiceReportsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useSearchAdminInvoiceReportsHook>>
        >,
        TError,
        TData
      >
    >;
    request?: SecondParameter<ReturnType<typeof useCustomClient>>;
  }
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getSearchAdminInvoiceReportsQueryKey(
      searchAdminInvoiceReportsRequest,
      params
    );

  const searchAdminInvoiceReports = useSearchAdminInvoiceReportsHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useSearchAdminInvoiceReportsHook>>>
  > = () =>
    searchAdminInvoiceReports(
      searchAdminInvoiceReportsRequest,
      params,
      requestOptions
    );

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useSearchAdminInvoiceReportsHook>>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type SearchAdminInvoiceReportsQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useSearchAdminInvoiceReportsHook>>>
>;
export type SearchAdminInvoiceReportsQueryError = ErrorType<
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse
  | UnprocessableEntityResponse
>;

/**
 * @summary Get a list of admin invoice reports
 */
export const useSearchAdminInvoiceReports = <
  TData = Awaited<
    ReturnType<ReturnType<typeof useSearchAdminInvoiceReportsHook>>
  >,
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
    | UnprocessableEntityResponse
  >
>(
  searchAdminInvoiceReportsRequest: SearchAdminInvoiceReportsRequest,
  params?: SearchAdminInvoiceReportsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useSearchAdminInvoiceReportsHook>>
        >,
        TError,
        TData
      >
    >;
    request?: SecondParameter<ReturnType<typeof useCustomClient>>;
  }
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = useSearchAdminInvoiceReportsQueryOptions(
    searchAdminInvoiceReportsRequest,
    params,
    options
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

export const useSearchAdminInvoiceReportsSuspenseQueryOptions = <
  TData = Awaited<
    ReturnType<ReturnType<typeof useSearchAdminInvoiceReportsHook>>
  >,
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
    | UnprocessableEntityResponse
  >
>(
  searchAdminInvoiceReportsRequest: SearchAdminInvoiceReportsRequest,
  params?: SearchAdminInvoiceReportsParams,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useSearchAdminInvoiceReportsHook>>
        >,
        TError,
        TData
      >
    >;
    request?: SecondParameter<ReturnType<typeof useCustomClient>>;
  }
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getSearchAdminInvoiceReportsQueryKey(
      searchAdminInvoiceReportsRequest,
      params
    );

  const searchAdminInvoiceReports = useSearchAdminInvoiceReportsHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useSearchAdminInvoiceReportsHook>>>
  > = () =>
    searchAdminInvoiceReports(
      searchAdminInvoiceReportsRequest,
      params,
      requestOptions
    );

  return { queryKey, queryFn, ...queryOptions } as UseSuspenseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useSearchAdminInvoiceReportsHook>>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type SearchAdminInvoiceReportsSuspenseQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useSearchAdminInvoiceReportsHook>>>
>;
export type SearchAdminInvoiceReportsSuspenseQueryError = ErrorType<
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse
  | UnprocessableEntityResponse
>;

/**
 * @summary Get a list of admin invoice reports
 */
export const useSearchAdminInvoiceReportsSuspense = <
  TData = Awaited<
    ReturnType<ReturnType<typeof useSearchAdminInvoiceReportsHook>>
  >,
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
    | UnprocessableEntityResponse
  >
>(
  searchAdminInvoiceReportsRequest: SearchAdminInvoiceReportsRequest,
  params?: SearchAdminInvoiceReportsParams,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useSearchAdminInvoiceReportsHook>>
        >,
        TError,
        TData
      >
    >;
    request?: SecondParameter<ReturnType<typeof useCustomClient>>;
  }
): UseSuspenseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = useSearchAdminInvoiceReportsSuspenseQueryOptions(
    searchAdminInvoiceReportsRequest,
    params,
    options
  );

  const query = useSuspenseQuery(queryOptions) as UseSuspenseQueryResult<
    TData,
    TError
  > & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey;

  return query;
};
